/**
 * Whole-upload XLSX budget check.
 *
 * XLSX cannot be streamed: SheetJS materializes the entire workbook in the browser
 * (see client/uploader/parse-stream.ts), so a single upload carrying too many — or
 * too many megabytes of — Excel files spikes the browser tab's memory. On top of
 * the per-file XLSX_SIZE_GUARD_BYTES cap, the uploader bounds each upload by XLSX
 * file COUNT and AGGREGATE bytes. The server never parses XLSX (it ingests only
 * scrubbed CSV), so this guard is purely client-side.
 *
 * Pure + isomorphic, and takes only {format, size} per staged file (no File handle,
 * no parse) so the file-select step can run it BEFORE preflight reads — refusing an
 * over-budget batch before any workbook is materialized is the whole point.
 */
import type { UploadFormat } from './pipeline-types';
import { MAX_XLSX_AGGREGATE_BYTES, MAX_XLSX_FILES_PER_UPLOAD } from './uploader-constants';

export interface XlsxBatchFile {
  readonly format: UploadFormat;
  /** File size in bytes (0 when the raw handle is unavailable, e.g. a reopened draft). */
  readonly size: number;
}

/** The first budget rule a batch trips, with the numbers needed to explain it. */
export type XlsxBatchViolation =
  | { readonly kind: 'too-many-files'; readonly count: number; readonly max: number }
  | { readonly kind: 'aggregate-too-large'; readonly bytes: number; readonly maxBytes: number };

/**
 * Returns the first XLSX-budget violation for `files`, or null when the batch is
 * within budget. CSV files are ignored — they stream with constant memory.
 */
export function checkXlsxBatch(files: readonly XlsxBatchFile[]): XlsxBatchViolation | null {
  const xlsx = files.filter((f) => f.format === 'xlsx');
  if (xlsx.length > MAX_XLSX_FILES_PER_UPLOAD) {
    return { kind: 'too-many-files', count: xlsx.length, max: MAX_XLSX_FILES_PER_UPLOAD };
  }
  const bytes = xlsx.reduce((total, f) => total + f.size, 0);
  if (bytes > MAX_XLSX_AGGREGATE_BYTES) {
    return { kind: 'aggregate-too-large', bytes, maxBytes: MAX_XLSX_AGGREGATE_BYTES };
  }
  return null;
}
